import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Zap, Globe, User, LogIn, LayoutDashboard, ChevronDown, ShoppingCart, Glasses, Building2, GraduationCap, Scan, QrCode, Briefcase, MoreHorizontal, Sparkles, Crown, Users, MessageSquare, Link2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage, SUPPORTED_LANGUAGES } from '@/i18n/LanguageContext';
import { Link } from 'react-router-dom';
import MobileLoginQR from '@/components/auth/MobileLoginQR';
import { Storage } from '@/components/auth/AuthProvider';
import { API_URL } from '@/utils/apiUrl';


// Ana nav itemleri (sabit linkler)
const navItems = [
  { key: 'nexus', href: '/nexus', label: 'Nexus AI' },
  { key: 'platform', href: '#about' },
  { key: 'engines', href: '#engines' },
  { key: 'projects', href: '/projects' },
  { key: 'demo', href: '#studio' },
  { key: 'applications', href: '#applications' },
  { key: 'contact', href: '#contact' },
];

// Dropdown içinde gösterilecek feature linkler
const moreLinks = [
  { key: 'pricing', href: '/pricing', icon: Crown },
  { key: 'about', href: '/about', icon: Users },
  { key: 'solutions', href: '/solutions', icon: ShoppingCart },
  { key: 'integrations', href: '/integrations', icon: Link2 },
  { key: 'ar', href: '/ar', icon: Glasses },
  { key: 'scanner', href: '/scanner', icon: Scan },
  { key: 'companies', href: '/companies', icon: Building2 },
  { key: 'training', href: '/training', icon: GraduationCap },
  { key: 'caseStudies', href: '/case-studies', icon: Briefcase },
  { key: 'faq', href: '/faq', icon: MoreHorizontal },
  { key: 'blog', href: '/blog', icon: Sparkles },
  { key: 'contact', href: '/contact', icon: MessageSquare },
];

// More Dropdown Component
function MoreDropdown({ t }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors text-slate-300 hover:text-white text-sm font-medium"
        data-testid="more-dropdown-btn"
      >
        <MoreHorizontal className="w-4 h-4" />
        <span>{t('nav.more') || 'More'}</span>
        <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full right-0 mt-2 w-56 bg-slate-900/95 backdrop-blur-xl border border-slate-700 rounded-xl shadow-2xl py-2 z-50"
          >
            {moreLinks.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.key}
                  to={item.href}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 text-slate-300 hover:text-white hover:bg-white/10 transition-colors"
                  data-testid={`more-${item.key}`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-sm font-medium">{t(`nav.${item.key}`)}</span>
                </Link>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Language Dropdown Component
function LanguageDropdown({ language, setLanguage, getCurrentLanguage }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const currentLang = getCurrentLanguage();
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
        data-testid="language-dropdown"
      >
        <span className="text-lg">{currentLang.flag}</span>
        <span className="text-sm font-medium text-slate-300">{currentLang.code.toUpperCase()}</span>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute top-full right-0 mt-2 w-48 bg-slate-900 border border-slate-700 rounded-xl shadow-xl overflow-hidden z-50"
          >
            <div className="max-h-80 overflow-y-auto">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => {
                    setLanguage(lang.code);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-slate-800 transition-colors ${
                    language === lang.code ? 'bg-blue-500/20 text-blue-300' : 'text-slate-300'
                  }`}
                >
                  <span className="text-lg">{lang.flag}</span>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{lang.nativeName}</div>
                    <div className="text-xs text-slate-500">{lang.name}</div>
                  </div>
                  {language === lang.code && (
                    <span className="w-2 h-2 bg-blue-500 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showMobileQR, setShowMobileQR] = useState(false);
  const { language, setLanguage, t, getCurrentLanguage } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Check if user is logged in - use centralized Storage helper
  useEffect(() => {
    const checkAuth = async () => {
      // First check storage (works for Safari/Mobile)
      const storedToken = Storage.get('session_token');
      const storedUser = Storage.get('user');
      const isLoggedIn = Storage.get('isLoggedIn');
      
      console.log('Header auth check:', { hasToken: !!storedToken, hasUser: !!storedUser, isLoggedIn });
      
      // Parse cached data only as a compatibility check. It must never render an
      // authenticated header before the server verifies the token: a stale
      // profile previously showed "Dashboard" while generation correctly ran
      // as an anonymous demo.
      if (storedToken && storedUser && isLoggedIn === 'true') {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (!parsedUser || typeof parsedUser !== 'object') Storage.clearAuth();
        } catch (e) {
          console.error('Failed to parse stored user');
          Storage.clearAuth();
        }
      }
      
      // Fallback: verify with API (using token from storage)
      if (storedToken) {
        try {
          const response = await fetch(`${API_URL}/api/auth/me`, {
            credentials: 'include',
            headers: {
              'Authorization': `Bearer ${storedToken}`
            }
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            // Update storage
            Storage.set('user', JSON.stringify(userData));
            Storage.set('isLoggedIn', 'true');
            // Save email for admin check
            if (userData.email) {
              localStorage.setItem('user_email', userData.email);
              sessionStorage.setItem('user_email', userData.email);
            }
          } else {
            // Token invalid, clear storage
            Storage.clearAuth();
            setUser(null);
          }
        } catch (error) {
          // Network error, keep storage data if exists
          console.log('Auth check failed, using cached data');
        }
      } else {
        Storage.clearAuth();
        setUser(null);
      }
    };
    
    checkAuth();
    
    // Re-check when localStorage changes (for cross-tab sync)
    const handleStorageChange = (e) => {
      if (e.key === 'isLoggedIn' || e.key === 'user') {
        checkAuth();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  const handleLogin = () => {
    // Redirect to /auth/callback which handles the session_id exchange
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `${API_URL}/api/auth/google/login?redirect_uri=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <>
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-[#060710]/80 backdrop-blur-xl border-b border-white/10 py-3' : 'py-5'}`}
      data-testid="header"
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3 group" data-testid="logo">
          <div className="w-8 h-8 bg-white flex items-center justify-center group-hover:bg-white/90 transition-colors">
            <Zap className="w-4 h-4 text-black" />
          </div>
          <span className="font-bold text-lg text-white tracking-tight">NEXUS</span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            <a
              key={item.key}
              href={item.href}
              className="text-white/50 hover:text-white transition-colors text-sm tracking-wide"
              data-testid={`nav-${item.key}`}
            >
              {item.label || t(`nav.${item.key}`)}
            </a>
          ))}
          
          {/* More Dropdown for extra links */}
          <MoreDropdown t={t} />
        </div>

        <div className="hidden md:flex items-center gap-4">
          <LanguageDropdown 
            language={language} 
            setLanguage={setLanguage} 
            getCurrentLanguage={getCurrentLanguage}
          />
          
          {user ? (
            <>
              <button
                onClick={() => setShowMobileQR(true)}
                className="flex items-center gap-2 px-3 py-2 rounded-full bg-cyan-500/20 hover:bg-cyan-500/30 transition-colors border border-cyan-500/30"
                data-testid="qr-login-btn"
                aria-label={language === 'tr' ? 'Mobil giriş QR kodu' : 'Mobile login QR code'}
                title="Mobil Giriş QR"
              >
                <QrCode className="w-4 h-4 text-cyan-400" />
              </button>
              <a
                href="/dashboard"
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/20 hover:bg-emerald-500/30 transition-colors border border-emerald-500/30"
                data-testid="dashboard-btn"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-5 h-5 rounded-full" />
                ) : (
                  <User className="w-4 h-4 text-white" />
                )}
                <span className="text-sm text-white">
                  {t('common.dashboard')}
                </span>
              </a>
            </>
          ) : (
            <a
              href={`${API_URL}/api/auth/google/login?redirect_uri=${encodeURIComponent(typeof window !== 'undefined' ? window.location.origin + '/auth/callback' : '')}`}
              className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white/80 hover:bg-white/5 hover:border-white/30 transition-all text-sm"
              data-testid="login-btn"
            >
              <LogIn className="w-4 h-4" />
              {t('common.login')}
            </a>
          )}
          
          <button
            className="px-4 py-2 bg-white text-black font-medium hover:bg-white/90 transition-colors text-sm"
            data-testid="request-demo-btn"
            onClick={() => document.getElementById('studio')?.scrollIntoView({ behavior: 'smooth' })}
          >
            {language === 'tr' ? 'Demo' : 'Try Demo'}
          </button>
        </div>

        <div className="flex md:hidden items-center gap-2">
          <LanguageDropdown 
            language={language} 
            setLanguage={setLanguage} 
            getCurrentLanguage={getCurrentLanguage}
          />
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className="text-white p-2"
            data-testid="mobile-menu-toggle"
            aria-label={isMobileOpen ? (language === 'tr' ? 'Menüyü kapat' : 'Close menu') : (language === 'tr' ? 'Menüyü aç' : 'Open menu')}
            aria-expanded={isMobileOpen}
          >
            {isMobileOpen ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden glass border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-6 space-y-4">
              {navItems.map((item) => (
                <a
                  key={item.key}
                  href={item.href}
                  className="block text-slate-400 hover:text-white transition-colors font-medium py-2"
                  onClick={() => setIsMobileOpen(false)}
                >
                  {item.label || t(`nav.${item.key}`)}
                </a>
              ))}
              
              {/* Feature Links for Mobile */}
              <div className="border-t border-slate-700 pt-4 mt-4">
                <p className="text-xs text-slate-500 uppercase mb-3">{language === 'tr' ? 'Özellikler' : 'Features'}</p>
                {moreLinks.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.key}
                      to={item.href}
                      className="flex items-center gap-3 text-slate-400 hover:text-white transition-colors font-medium py-3"
                      onClick={() => setIsMobileOpen(false)}
                    >
                      <Icon className="w-5 h-5" />
                      {t(`nav.${item.key}`)}
                    </Link>
                  );
                })}
              </div>
              
              {user ? (
                <a
                  href="/dashboard"
                  className="flex items-center gap-2 px-4 py-3 rounded-lg bg-emerald-500/20 border border-emerald-500/30"
                  onClick={() => setIsMobileOpen(false)}
                >
                  <LayoutDashboard className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-300">{t('common.dashboard')}</span>
                </a>
              ) : (
                <a
                  href={`${API_URL}/api/auth/google/login?redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}`}
                  className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-full font-medium"
                >
                  <LogIn className="w-4 h-4" />
                  {t('common.login')} (Google)
                </a>
              )}
              
              <Button className="w-full bg-gradient-to-r from-blue-600 to-violet-600 text-white rounded-full mt-2">
                {t('hero.cta1')}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
    
    {/* Mobile Login QR Modal */}
    <MobileLoginQR isOpen={showMobileQR} onClose={() => setShowMobileQR(false)} />
  </>
  );
}
